import { useCallback, useEffect, useRef, useState } from 'react';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import LoadingBar from 'react-top-loading-bar';

import type {
  IIndeterminateProgressCompleteState,
  IIndeterminateProgressOptions,
} from '~/helpers/IndeterminateProgress';
import { IndeterminateProgress } from '~/helpers/IndeterminateProgress';
import { ITimeUnit } from '~/helpers/types';

export type ITopLoadingBarProps = Partial<
  Pick<
    IIndeterminateProgressOptions,
    | 'maxNonDeterministicProgress'
    | 'expectedProgress'
    | 'atExpectedTime'
    | 'minCompletionDuration'
    | 'maxCompletionDuration'
  >
> & {
  /**
   * Whether the loading bar is loading or not.
   */
  loading?: boolean;

  /**
   * The minimum time to wait before showing the loading bar. This is useful to
   * prevent the loading bar from flashing for very short requests.
   */
  showAfterMinLoadingTime?: number;

  /**
   * The initial interval to use when updating the loading bar.
   */
  updateInitialInterval?: number;

  /**
   * A function to call to increase the update interval. This is useful to slow
   * down the loading bar as it progresses.
   */
  updateIntervalIncreaseFunc?: (interval: number) => number;

  /**
   * The delay to wait before completing the loading bar after all requests have
   * finished. This is useful to prevent the loading bar from completing and
   * then starting again too quickly.
   */
  completeDelay?: number;
};

export const TopLoadingBar: React.FC<ITopLoadingBarProps> = (props) => {
  const {
    loading,
    showAfterMinLoadingTime = 0,
    updateInitialInterval = 150 * ITimeUnit.MILLISECOND,
    updateIntervalIncreaseFunc = (interval: number): number => interval,
    completeDelay = 150 * ITimeUnit.MILLISECOND,
    ...indeterminateProgressOptions
  } = props;

  const [progress, setProgress] = useState(0);
  const startedAtRef = useRef<number | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | undefined>();
  const completeTimeoutRef = useRef<NodeJS.Timeout | undefined>();
  const updateInterval = useRef(updateInitialInterval);
  const completeStateRef = useRef<
    IIndeterminateProgressCompleteState | undefined
  >();
  const loadingRef = useRef(false);
  const visibleRef = useRef(false);

  const indeterminateProgress = useRef(
    new IndeterminateProgress(indeterminateProgressOptions),
  );

  const update = useCallback(() => {
    if (!startedAtRef.current) {
      return;
    }

    const elapsedTime = +new Date() - startedAtRef.current;
    const progress = indeterminateProgress.current.getProgress(
      elapsedTime,
      completeStateRef.current,
    );

    visibleRef.current = elapsedTime > showAfterMinLoadingTime;
    if (visibleRef.current) {
      setProgress(progress);
    }

    updateInterval.current = updateIntervalIncreaseFunc(updateInterval.current);
    updateTimeoutRef.current = setTimeout(update, updateInterval.current);
  }, [showAfterMinLoadingTime, updateIntervalIncreaseFunc]);

  useEffect(() => {
    if (loading) {
      completeStateRef.current = undefined;

      if (!startedAtRef.current) {
        loadingRef.current = true;
        startedAtRef.current = +new Date();
        updateInterval.current = updateInitialInterval;

        setProgress(0);
        update();
      }
    } else if (startedAtRef.current) {
      const elapsedTime = +new Date() - startedAtRef.current;
      completeStateRef.current = {
        dt: +new Date() - startedAtRef.current,
        progress: indeterminateProgress.current.getProgress(elapsedTime),
      };

      loadingRef.current = false;

      // Prevent the loading bar from completing and starting again too
      // quickly.
      clearTimeout(completeTimeoutRef.current);
      completeTimeoutRef.current = setTimeout(() => {
        if (!loadingRef.current) {
          clearTimeout(updateTimeoutRef.current);
          startedAtRef.current = null;

          if (visibleRef.current) {
            setProgress(1);
          }
        }
      }, completeDelay);
    }
  }, [
    loading,
    showAfterMinLoadingTime,
    updateInitialInterval,
    completeDelay,
    update,
  ]);

  return (
    <LoadingBar
      progress={progress * 100}
      color={colorSchemeTokens.primary}
      height={4}
    />
  );
};
